'use client';

import { ReactNode, ElementType } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import cx from 'classnames';
import { styled, Palette, SxProps, Theme, transientOptions } from '../styles';

import Typography from '../Typography';
import type { Variant as TypographyVariant } from '../Typography';
import Box from '../Box';

const getColor = (palette: Palette, color: TagTypes, variant: TagVariant): object => {
  const availableStyles: Partial<Record<TagTypes, Record<TagVariant, any>>> = {
    purple: {
      primary: {
        backgroundColor: palette.tag.purple.main,
        color: palette.tag.purple.onMain,
      },
      secondary: {
        backgroundColor: palette.tag.purple.secondary,
        color: palette.tag.purple.onSecondary,
      },
    },
    green: {
      primary: {
        backgroundColor: palette.tag.green.main,
        color: palette.tag.green.onMain,
      },
      secondary: {
        backgroundColor: palette.tag.green.secondary,
        color: palette.tag.green.onSecondary,
      },
    },
    blue: {
      primary: {
        backgroundColor: palette.tag.blue.main,
        color: palette.tag.blue.onMain,
      },
      secondary: {
        backgroundColor: palette.tag.blue.secondary,
        color: palette.tag.blue.onSecondary,
      },
    },
    orange: {
      primary: {
        backgroundColor: palette.tag.orange.main,
        color: palette.tag.orange.onMain,
      },
      secondary: {
        backgroundColor: palette.tag.orange.secondary,
        color: palette.tag.orange.onSecondary,
      },
    },
    red: {
      primary: {
        backgroundColor: palette.tag.red.main,
        color: palette.tag.red.onMain,
      },
      secondary: {
        backgroundColor: palette.tag.red.secondary,
        color: palette.tag.red.onSecondary,
      },
    },
    crimson: {
      primary: {
        backgroundColor: palette.tag.crimson.main,
        color: palette.tag.crimson.onMain,
      },
      secondary: {
        backgroundColor: palette.tag.crimson.secondary,
        color: palette.tag.crimson.onSecondary,
      },
    },
    yellow: {
      primary: {
        backgroundColor: palette.tag.yellow.main,
        color: palette.tag.yellow.onMain,
      },
      secondary: {
        backgroundColor: palette.tag.yellow.secondary,
        color: palette.tag.yellow.onSecondary,
      },
    },
    grey: {
      primary: {
        backgroundColor: palette.tag.grey.main,
        color: palette.tag.grey.onMain,
      },
      secondary: {
        backgroundColor: palette.tag.grey.secondary,
        color: palette.tag.grey.onSecondary,
      },
    },
    neutral: {
      primary: {
        backgroundColor: palette.tag.neutral.main,
        color: palette.tag.neutral.onMain,
      },
      secondary: {
        backgroundColor: palette.tag.neutral.secondary,
        color: palette.tag.neutral.onSecondary,
      },
    },
  };

  availableStyles.success = availableStyles.green;
  availableStyles.warning = availableStyles.orange;
  availableStyles.danger = availableStyles.red;
  availableStyles.info = availableStyles.blue;

  return availableStyles[color]?.[variant];
};

const getHeight = (size: TagSize): string => {
  const sizeToHeight: Record<TagSize, string> = {
    small: '22px',
    medium: '30px',
  };
  return sizeToHeight[size];
};

const Root = styled(
  Box,
  transientOptions
)<{ $color: TagTypes; $size: TagSize; $variant: TagVariant }>(
  ({ theme: { spacing, shape, palette }, $color, $size, $variant }) => ({
    flexShrink: 0,
    width: 'fit-content',
    position: 'relative',
    borderRadius: shape.borderRadius,
    padding: spacing(0, 1),
    minWidth: 56,
    textAlign: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    height: getHeight($size),
    ...getColor(palette, $color, $variant),
    '.MuiSvgIcon-root': {
      paddingRight: spacing(0.5),
    },
  })
);

const Label = styled(Typography)(() => ({
  color: 'inherit',
}));

export interface Props {
  children: ReactNode;
  classes?: object;
  color?: TagTypes;
  size?: TagSize;
  variant?: TagVariant;
  sx?: SxProps<Theme>;
  className?: string;
  icon?: ReactNode;
  component?: ElementType;
}

export type TagTypes =
  | 'purple'
  | 'green'
  | 'blue'
  | 'orange'
  | 'red'
  | 'crimson'
  | 'yellow'
  | 'grey'
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

type TagSize = 'small' | 'medium';

type TagVariant = 'primary' | 'secondary';

const Tag = ({
  children,
  classes: customClasses = {},
  color = 'neutral',
  variant = 'primary',
  size: tagSize = 'medium',
  className,
  sx,
  icon,
  component = 'span',
}: Props) => {
  const sizeToVariant: Record<TagSize, TypographyVariant> = {
    small: 'subheadingSmall',
    medium: 'subheading',
  };
  const variantSize = sizeToVariant[tagSize];

  return (
    <Root
      className={cx(customClasses, className)}
      $color={color}
      $size={tagSize}
      $variant={variant}
      sx={sx}
      data-testid="tag"
      as={component}
    >
      {icon}
      <Label variant={variantSize} component="span">
        {children}
      </Label>
    </Root>
  );
};

export default Tag;
